import { api, client } from '@/shared/api/generated'
import type { UserResponse, GroupResponse, MemberResponse, PermissionJSON, ViewResponse, ViewMemberResponse, CreateViewRequest, UpdateGroupRequest, UserPermissionJSON } from '@/shared/api/generated'

export interface DownloadUsage {
  hourly: number
  daily: number
  hourly_limit: number
  daily_limit: number
}

export const adminApi = {
  listUsers: async (): Promise<UserResponse[]> => {
    const resp = await api.adminListUsers()
    return resp.users
  },

  listDeletedUsers: async (): Promise<UserResponse[]> => {
    const resp = await api.adminListDeletedUsers()
    return resp.users
  },

  createUser: (username: string, password: string): Promise<void> =>
    api.adminCreateUser({ username, password, site_permission: 'user' }).then(() => {}),

  deleteUser: (userId: number): Promise<void> =>
    api.adminDeleteUser(userId).then(() => {}),

  restoreUser: (userId: number): Promise<void> =>
    api.adminRestoreUser(userId).then(() => {}),

  resetPassword: (userId: number, newPassword: string): Promise<void> =>
    api.adminResetPassword(userId, { new_password: newPassword }).then(() => {}),

  listGroups: (): Promise<GroupResponse[]> => api.listGroups(),

  updateGroup: (groupId: number, req: UpdateGroupRequest): Promise<void> =>
    api.updateGroup(groupId, req).then(() => {}),

  getGroupMembers: (groupId: number): Promise<MemberResponse[]> => api.listGroupMembers(groupId),

  addGroupMember: (groupId: number, username: string, role: string): Promise<void> =>
    api.addGroupMember(groupId, { username, role }).then(() => {}),

  removeGroupMember: (groupId: number, userId: number): Promise<void> =>
    api.removeGroupMember(groupId, userId).then(() => {}),

  updateMemberPermissions: (groupId: number, userId: number, perms: PermissionJSON): Promise<void> =>
    api.updateMemberPermissions(groupId, userId, perms).then(() => {}),

  updateMemberRole: (groupId: number, userId: number, role: string): Promise<void> =>
    api.updateMemberRole(groupId, userId, { role }).then(() => {}),

  listAllUsers: async (): Promise<UserResponse[]> => {
    const resp = await api.listAllUsers()
    return resp.users
  },

  setUserPermissions: (userId: number, perms: UserPermissionJSON): Promise<void> =>
    api.adminSetUserPermissions(userId, perms).then(() => {}),

  updatePermission: (userId: number, sitePermission: string): Promise<void> =>
    api.adminUpdatePermission(userId, { site_permission: sitePermission }).then(() => {}),

  disable2FA: (userId: number): Promise<void> =>
    api.adminDisable2FA(userId).then(() => {}),

  getUserDevices: (userId: number): Promise<import('@/shared/api/generated').DeviceResponse[]> =>
    api.adminGetUserDevices(userId),

  revokeUserDevice: (userId: number, deviceId: string): Promise<void> =>
    api.adminRevokeUserDevice(userId, deviceId),

  // Views
  listGroupViews: (groupId: number): Promise<ViewResponse[]> => api.listGroupViews(groupId),

  createView: (groupId: number, req: CreateViewRequest): Promise<ViewResponse> => api.createView(groupId, req),

  deleteView: (viewId: number): Promise<void> => api.deleteView(viewId).then(() => {}),

  updateView: (viewId: number, req: CreateViewRequest): Promise<void> => api.updateView(viewId, req).then(() => {}),

  listViewMembers: (viewId: number): Promise<ViewMemberResponse[]> => api.listViewMembers(viewId),

  addViewMember: (viewId: number, username: string, perms: PermissionJSON): Promise<void> =>
    api.addViewMember(viewId, { username, permissions: perms }).then(() => {}),

  removeViewMember: (viewId: number, userId: number): Promise<void> =>
    api.removeViewMember(viewId, userId).then(() => {}),

  updateViewMemberPermissions: (viewId: number, userId: number, perms: PermissionJSON): Promise<void> =>
    api.updateViewMemberPermissions(viewId, userId, perms).then(() => {}),

  setDownloadLimits: async (userId: number, hourly: number, daily: number): Promise<void> => {
    await client.put(`/admin/users/${userId}/download-limits`, { hourly, daily })
  },

  getDownloadUsage: async (userId: number): Promise<DownloadUsage> => {
    const { data } = await client.get<DownloadUsage>(`/admin/users/${userId}/download-usage`)
    return data
  },
}
